import { FunctionComponent, useCallback } from 'react';
import Head from 'next/head';
import { H1, H2, H4, Text } from 'components/Typography/Typography';
import { Layout, LayoutWrapper } from 'components/Layout/Layout';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import { Header } from 'components/Layout/Header';
import { Footer } from 'components/Layout/Footer';
import { scrollTop } from 'utils';

const TermsAndConditions: FunctionComponent = () => {
  const router = useRouter();

  const onRoute = useCallback(
    (path) => {
      router.push(path);
      scrollTop();
    },
    [router, scrollTop],
  );

  return (
    <Layout>
      <Head>
        <title>The Earth Prize - Terms And Conditions</title>
        <meta
          name="description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta
          property="og:title"
          content="The Earth Prize - Terms And Conditions"
        />
        <meta
          property="og:description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <Header />
      <TermsAndConditionsWrapper>
        <H1>TERMS & CONDITIONS</H1>
        <Text>
          <b>Effective from:</b> 1st February, 2024
        </Text>
        <br />
        <Text>
          <i>
            These The Earth Prize Terms & Conditions explain the rules that
            apply to your use of this website.
            <br />
            <br />
            Please read this document carefully. If you do not agree with one or
            more statements that you find here, you should not use this website.
          </i>
        </Text>
        <br />
        <a href="#SOME-GENERAL-INFORMATION">
          <H4>1. SOME GENERAL INFORMATION ABOUT US AND THIS WEBSITE</H4>
        </a>
        <a href="#WHAT-CANT-YOU-DO">
          <H4>2. WHAT CAN’T YOU DO ON THE WEBSITE?</H4>
        </a>
        <a href="#HOW-TO-REGISTER">
          <H4>3. HOW TO REGISTER AND KEEP YOUR ACCOUNT?</H4>
        </a>
        <a href="#IS-THE-WEBSITE">
          <H4>4. IS THE WEBSITE ALWAYS ACCESSIBLE?</H4>
        </a>
        <a href="#OUR-WARRANTIES">
          <H4>5. OUR WARRANTIES</H4>
        </a>
        <a href="#OUR-LIABILITY">
          <H4>6. OUR LIABILITY</H4>
        </a>
        <a href="#INDEMNIFICATION">
          <H4>7. INDEMNIFICATION</H4>
        </a>
        <a href="#SEVERITY">
          <H4>8. SEVERITY</H4>
        </a>
        <a href="#GOVERNING-LAW">
          <H4>9. GOVERNING LAW AND DISPUTES</H4>
        </a>
        <a href="#FINAL-NOTES">
          <H4>10. FINAL NOTES</H4>
        </a>
        <a href="#CONTACT">
          <H4>11. CONTACT</H4>
        </a>
        <br />
        <H2>
          <span id="SOME-GENERAL-INFORMATION" />
          1. SOME GENERAL INFORMATION ABOUT US AND THIS WEBSITE
        </H2>
        <Text>
          1.1 <b>What is this website about?</b> This website is used to invite
          you to participate in a global competition entitled ‘The Earth Prize’
          and win our incredible environmental sustainability prize.
          <br />
          <br />
          1.2 <b>Owner of the website.</b> This website (theearthprize.org) is
          owned and operated by us, The Earth Foundation. We have an address at
          Boulevard du théâtre 5, 1204 Genève, Switzerland. Our registration
          number is CHE-439.560.973. When you decide to start using this
          website, you enter into agreement with us stating that these The Earth
          Prize Terms & Conditions (we will call them “Terms” for the sake of
          simplicity) will govern your use of the website and you agree to
          comply with all rules stated in this document.
          <br />
          <br />
          1.3 <b>License to use the website.</b> If you agree to these Terms, we
          grant you a worldwide, personal, revocable, non-exclusive,
          non-transferable and limited license to use this website.
          <br />
          <br />
          1.4 <b>Disclaimer.</b> We regularly monitor the information available
          on this website to make sure that there are no mistakes or
          inaccuracies. However, we cannot guarantee that all information that
          you see on this website is always accurate, reliable, current,
          relevant, or complete.
          <br />
          <br />
          1.5 <b>Other websites.</b> The website may contain links to other
          websites that are not owned by us (for example, you can find links to
          our social media networks). We are not responsible for the content of
          such websites (by “content”, we mean links, information,
          advertisements, security, and privacy) because the owners of those
          websites take care of that content. Also, these Terms do not apply to
          other websites. You should always be very careful before accessing
          other websites or clicking on any links that are not on our website.
          <br />
          <br />
          1.6 <b>Can this website be used by children?</b>
          <br />
          <br />
        </Text>
        <ul>
          <li>If you are older than 18, you can freely use this website.</li>
          <li>
            If your age is between 13 and 18, you have to ask your parent or
            another adult who is responsible for taking care of you a permission
            to use this website. You have to also make sure that you have read
            these Terms together with such an adult person and you fully
            understand them. If you do not understand any part of these Terms,
            please contact us and we will help you. We may ask your parent or
            the person who allows you to use the website to verify the
            permission.
          </li>
          <li>
            Children younger than 13 are not allowed to access this website.
          </li>
        </ul>
        <Text>
          <br />
          1.8 <b>What else should you read?</b> In addition to these Terms, you
          should also read other documents that are important for your use of
          this website:
          <br />
          <br />
        </Text>
        <ul>
          <li>
            Our{' '}
            <span onClick={() => onRoute('/privacy-policy')} aria-hidden="true">
              privacy policy
            </span>{' '}
            and{' '}
            <span onClick={() => onRoute('/cookie-policy')} aria-hidden="true">
              cookie policy
            </span>
            , which explain what we do with your personal data that you submit
            through the website;
          </li>
          <li>
            Our{' '}
            <span
              onClick={() => onRoute('/copyright-policy')}
              aria-hidden="true"
            >
              copyright policy
            </span>
            , which explains how we protect our content and your input;
          </li>
          <li>The Earth Prize Official Rules;</li>
          <li>The Earth Prize Code of Conduct; and</li>
          <li>The Earth Prize Terms and Conditions</li>
        </ul>
        <br />
        <H2>
          <span id="WHAT-CANT-YOU-DO" /> 2. WHAT CAN’T YOU DO ON THE WEBSITE?
        </H2>
        <Text>
          We want to make this website a pleasant and secure space for everyone.
          Therefore, whenever you use the website or submit your projects for
          The Earth Prize competition, you have to comply with our rules. If we
          see that someone does not comply with our rules, we will take measures
          to stop such behavior (for example, we can suspend or delete one’s
          account or report one to law enforcement authorities).
          <br />
          <br /> You are not allowed to use the website for any of the following
          activities:
        </Text>
        <br />
        <ul>
          <li>
            Infringing The Earth Prize Official Rules ,The Earth Prize Code of
            Conduct; and the Earth Prize Terms and Conditions
          </li>
          <li>Violating any laws;</li>
          <li>
            Committing cyber offenses like getting unauthorized access to
            machines, programs, or data;
          </li>
          <li>Engaging in fraudulent activities;</li>
          <li>Providing false, inaccurate, or misleading information;</li>
          <li>
            Spreading malware (e.g., viruses, worms, Trojan horses), spam, and
            other illegal materials;
          </li>
          <li>
            Disseminating information that can be perceived as ethnically,
            racially, or otherwise objectionable, sexually explicit, libelous,
            harassing, defamatory, abusive, profane, vulgar, threatening,
            hateful;
          </li>
          <li>Engaging in or encouraging obscene behavior;</li>
          <li>Spreading terrorism-related content;</li>
          <li>
            Causing troubles to the website like copying, distributing, renting,
            reselling, modifying, compromising, damaging its content, or
            disabling, impairing, and overburdening the website;
          </li>
          <li>Abusing other users of the website;</li>
          <li>
            Using bots, scripts, and other automated methods to access the
            website or scrape data from it; and
          </li>
          <li>
            Collecting and disclosing any information about other users of the
            website.
          </li>
        </ul>
        <br />
        <H2>
          <span id="HOW-TO-REGISTER" /> 3. HOW TO REGISTER AND KEEP YOUR
          ACCOUNT?
        </H2>
        <Text>
          3.1 <b>Registration of your Account.</b> In order to use the full
          functionality of the website (for example, to submit your project for
          The Earth Prize competition), you need to register a user account.
          There are three different types of user accounts. You must choose the
          account that is most suitable for you:
        </Text>
        <br />
        <ul>
          <li>
            If you are a student, you must register a student account (if you
            are between 13 and 18 years old, you are responsible for obtaining
            the necessary permissions as explained in section 1.7);
          </li>
          <li>
            If you are a parent of a student, you must register a parent
            account; and
          </li>
          <li>If you are a teacher, you must choose a teacher account.</li>
        </ul>
        <br />
        <Text>All registered and verified users have access to:</Text>
        <br />
        <ul>
          <li>The Earth Prize Submissions Portal;</li>
          <li>The Earth Prize Mentorship Portal; and</li>
          <li>The Earth Prize Learning Platform</li>
        </ul>
        <br />
        <Text>
          3.2 <b>Your personal data.</b> Before creating your account, you will
          be invited to read and accept these Terms and check our{' '}
          <a href="privacy-policy">privacy policy</a>. All personal data that
          you submit when signing up (for example, your name and email address)
          will be collected and used by us as explained in our{' '}
          <a href="privacy-policy">privacy policy</a>. You have to make sure
          that you are the sole owner of your account — you cannot transfer it
          to other persons. You are responsible for all activities, submissions,
          and communication occurring through your account.
          <br />
          <br />
          3.3 <b>Your warranties.</b> By registering your account on the
          website, you promise the following:
        </Text>
        <br />
        <ul>
          <li>You will comply with these Terms and all applicable laws;</li>
          <li>
            You will provide only true, accurate, complete, and up-to-date
            personal data and other information;
          </li>
          <li>
            You will update your account as soon as there are changes in your
            information;
          </li>
          <li>
            If you represent a school or another organization, you are actually
            a part of it;
          </li>
          <li>You will not register more than one account; and</li>
          <li>
            You are a human and not a machine (machine-generated accounts are
            not allowed).
          </li>
        </ul>
        <br />
        <Text>
          3.4 <b>Security of your account.</b> You are responsible for keeping
          secure your login details and passwords. This means that you should
          not disclose your details to other persons (whether online or not). If
          you notice that another person is using your account or there is
          another security issue with it, you must immediately notify us. Before
          opening this website, please make sure that the Internet connection
          that you use is safe and secure. We cannot and will not be responsible
          for any loss or damage that results from the lack of security from
          your side.
          <br />
          <br />
          3.5 <b>Deletion of your account.</b> At any time, you may delete your
          account through the dashboard of your account or by sending a request
          directly to us.
          <br />
          <br />
          3.6 <b>Suspension and termination of your account.</b> If we learn
          that your use of this website seriously and repeatedly breaches these
          Terms, we will suspend or terminate your account. We make this
          solution at our sole discretion.
        </Text>
        <br />
        <H2>
          <span id="IS-THE-WEBSITE" /> 4. IS THE WEBSITE ALWAYS ACCESSIBLE?
        </H2>
        <Text>
          We put reasonable efforts to ensure that the website is always
          accessible to you. However, sometimes, our website may be down due to
          unforeseen circumstances and factors, which we cannot control, such as
          bandwidth problems, equipment failure, acts and omissions of
          third-parties, or force majeure events like: acts of God; strikes;
          work stoppages; accidents; acts of war or terrorism; civil or military
          disturbances; nuclear or natural catastrophes and interruptions;
          shortage of supply, breakdowns, loss or malfunctions of utilities and
          communications or computer (software and hardware) services. We are
          not responsible if the website is unavailable due to such factors.
        </Text>
        <br />
        <H2>
          <span id="OUR-WARRANTIES" /> 5. OUR WARRANTIES
        </H2>
        <Text>
          5.1 We provide this website as you can see it, that is, on “as
          available”, “as is”, and “with all faults” bases. We do not make any
          representations or warranties about the reliability, suitability, and
          accuracy of the website or its content (whether provided by us or
          anyone else) and hereby disclaim all warranties regarding the website
          and its operation. Our disclaimer is valid to the extent the
          applicable law permits it.
          <br />
          <br />
          5.2 You must check beforehand whether the website is suitable for you
          prior to using it.
          <br />
          <br />
          5.3 From time to time, we may use our partners to help us with
          technical aspects of the website like software, storage, networking,
          email distribution, and similar services. We cannot entirely control
          our partners. Thus, to the maximum extent permitted by law, we exclude
          any liability for any loss or damage resulting from the acts and
          omissions of such third-party suppliers.
          <br />
          <br />
          5.4 If you are acting as a consumer while using the website, nothing
          in these Terms shall affect your statutory rights that you are
          entitled to.
        </Text>
        <br />
        <H2>
          <span id="OUR-LIABILITY" /> 6. OUR LIABILITY
        </H2>
        <Text>
          6.1 Unless the law states otherwise, we are not liable for any damages
          that may occur while you are using the website or in connection with
          the website.
          <br />
          <br />
          6.2 If you decide to conclude any transactions through the website or
          use the website for any unauthorized or unlawful purpose, we are not
          liable either.
          <br />
          <br />
          6.3 You agree not to hold us liable in respect of any losses arising
          out of any event or events beyond our reasonable control.
          <br />
          <br />
          6.3 This Section 6 shall apply whether or not we have been advised of
          or should have been aware of the possibility of any such losses
          arising.
        </Text>
        <br />
        <H2>
          <span id="INDEMNIFICATION" /> 7. INDEMNIFICATION
        </H2>
        <Text>
          You agree to indemnify and defend us, our subsidiaries, affiliates,
          partners, officers, directors, agents, contractors, licensors, service
          providers, subcontractors, suppliers, interns and employees, harmless
          from any claim or demand, including attorneys’ fees, made by any third
          party due to, or arising out of, your breach of these Terms, your use
          of the website, or your violation of any law or the rights of a third
          party. This means that, if there is an issue related to your use of
          the website, you agree to secure us against hurt, loss, and damage.
        </Text>
        <br />
        <H2>
          <span id="SEVERITY" /> 8. SEVERITY
        </H2>
        <Text>
          If any statement of these Terms becomes invalid, unlawful, void or
          unenforceable, such a statement will not affect the rest of the Terms
          - the remaining provisions of the Terms shall remain valid.
        </Text>
        <br />
        <H2>
          <span id="GOVERNING-LAW" /> 9. GOVERNING LAW AND DISPUTES
        </H2>
        <Text>
          9.1 <b>Governing law.</b> These Terms are governed by the laws of
          Switzerland (Canton of Genève), without regard to its conflicts of law
          provisions.
          <br />
          <br />
          9.2 <b>Jurisdiction.</b> If any disputes arise out of these Terms, you
          agree to resolve them by means of negotiation with us. If the dispute
          cannot be resolved by means of negotiation, the dispute shall be
          submitted to the courts in Genève, Switzerland. This Section 9 does
          not affect any statutory rights that you may be entitled to as a
          consumer.
        </Text>
        <br />
        <H2>
          <span id="FINAL-NOTES" /> 10. FINAL NOTES
        </H2>
        <Text>
          10.1 <b>How long are these Terms valid?</b> The Terms enter into force
          on the date indicated at the top of the Terms and remain in force
          until updated or terminated by us.
          <br />
          <br />
          10.2 <b>Can the Terms change?</b> We have the right to modify these
          Terms at any time. If this happens, we will specify the new effective
          date and post an updated version on this page. Me may decide to change
          the Terms if there are new rules and laws that we have to comply with
          or if the website changes. If we have your email address, we will send
          you a short notice informing you about any material amendments to the
          Terms that may be of importance to you. To make sure that you know the
          latest Terms, please review this page from time to time. If you
          continue using the website after the amendment date, it means that you
          agree with the changes.
          <br />
          <br />
          10.3 <b>What if you breach these Terms?</b> If we believe, at our sole
          discretion, that you breach these Terms and it is appropriate,
          necessary or desirable to do so, we reserve the right to:
        </Text>
        <br />
        <ul>
          <li>Send you a formal warning;</li>
          <li>Temporarily or permanently prohibit your use of the website;</li>
          <li>Report you to the relevant public authorities; or</li>
          <li>Commence a legal action against you.</li>
        </ul>
        <br />
        <Text>
          10.4 <b>Can you assign your rights and obligations?</b> No, you are
          not allowed to assign your rights or obligations under these Terms.
          <br />
          <br />
          10.5 <b>Can we transfer our rights and obligations?</b> Yes, we are
          entitled to transfer our rights and obligations under these Terms
          entirely or partially to a third party by giving a prior notice to you
          (if we deem it necessary). If you do not agree to the transfer, you
          can terminate these Terms with immediate effect by ceasing to use the
          website.
          <br />
          <br />
          10.6 <b>What if the owner of the website changes?</b> In the event the
          owner of the website or The Earth Foundation changes, these Terms
          shall not automatically be terminated and we agree to use our best
          efforts to ensure that the new owner assumes and is bound by the
          provisions of these Terms.
          <br />
          <br />
          10.7 <b>Entire agreement.</b> These Terms, together with the documents
          referred to therein, represent the entire agreement between you and us
          regarding your relationship with us and govern your use of the
          website.
        </Text>
        <br />
        <H2>
          <span id="CONTACT" /> 11. CONTACT
        </H2>
        <Text>
          If you have any questions about these Terms, please contact us by
          using the following contact details:
        </Text>
        <br />
        <AddressWrapper>
          <div>
            <Text>
              <b>Email:</b>
              <br />
              <b>Postal address:</b>
            </Text>
          </div>
          <div>
            <Text>
              info@theearthprize.org
              <br />
              The Earth Foundation
              <br />
              Boulevard du théâtre 5
              <br />
              1204 Genève
              <br />
              Switzerland
            </Text>
          </div>
        </AddressWrapper>
      </TermsAndConditionsWrapper>
      <Footer fullHeight />
    </Layout>
  );
};

const TermsAndConditionsWrapper = styled(LayoutWrapper)`
  ${({ theme: { Colors, Consts } }) => css`
    padding: 246px 80px 142px;
    background-color: ${Colors.TermsAndConditionsWrapperBackgroundColor};
    @media (max-width: ${Consts.tabletBreakPoint}) {
      padding: 100px 30px 121px;
    }

    > h1 {
      margin-bottom: 34px;
      @media (max-width: ${Consts.tabletBreakPoint}) {
        margin-bottom: 20px;
        font-size: 31px;
      }
    }

    > h2 {
      margin-bottom: 34px;
      font-size: 28px;

      > span {
        position: relative;
        top: -100px;
      }

      @media (max-width: ${Consts.tabletBreakPoint}) {
        margin-bottom: 20px;
        font-size: 20px;
      }
    }

    a {
      text-decoration: underline;

      h4 {
        font-size: 18px;
      }
    }

    > p {
      margin-bottom: 0;
    }

    > ul {
      padding-left: 30px;
      font-size: 14px;

      > li {
        font-size: 18px;
        margin-bottom: 10px;

        @media (max-width: ${Consts.tabletBreakPoint}) {
          font-size: 14px;
        }

        > span {
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  `}
`;

const AddressWrapper = styled.div`
  display: flex;
  align-items: flex-start;

  > div {
    margin-right: 20px;
  }
`;

export default TermsAndConditions;
